import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { useMemo } from 'react'

export type SubscriptionRow = {
  id: string
  company_id: string
  company_name: string
  company_logo: string | null
  stripe_subscription_id: string | null
  status: string
  plan_id: string | null
  plan_title: string | null
  plan_amount: number | null
  plan_interval: string | null
  current_period_end: string | null
  mrr: number
}

type SubscriptionsResponse = {
  data: SubscriptionRow[]
  total: number
  limit: number
  offset: number
}

type UseSubscriptionsParams = {
  limit?: number
  offset?: number
  sortField?: string
  sortDir?: 'asc' | 'desc'
  q?: string
  status?: string
  plan?: string
  interval?: string
}

type UseSubscriptionsResult = {
  data: SubscriptionsResponse | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export const useSubscriptionsQuery = (
  params: UseSubscriptionsParams = {},
  options?: { enabled?: boolean },
): UseSubscriptionsResult => {
  const stableParams = useMemo(() => {
    const base: Record<string, any> = {
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }
    if (params.sortField && params.sortDir) {
      base.sort = `${params.sortField}:${params.sortDir}`
    }
    if (params.q) {
      base.q = params.q
    }
    if (params.status) {
      base.status = params.status
    }
    if (params.plan) {
      base.plan = params.plan
    }
    if (params.interval) {
      base.interval = params.interval
    }
    return base
  }, [
    params.limit,
    params.offset,
    params.sortField,
    params.sortDir,
    params.q,
    params.status,
    params.plan,
    params.interval,
  ])

  const isEnabled = options?.enabled !== false

  const query = useQuery({
    queryKey: ['subscriptions', 'list', stableParams],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/internal/subscriptions',
        params: stableParams,
      })
      return response.data as SubscriptionsResponse
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 10,
  })

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
